import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useCurrentUser } from 'src/app/shared/hooks/session';
import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

export type PpmuLocalizedHeadlineData = {
	totalSignatureCount: number;
	userCountryName: string;
	userStateName?: string;
};

type CountryOrStateSignatureCounts =
	| {
			totalSignatureCount: number;
	  }
	| null
	| undefined;

function validateData(userCountryCode: string, userStateCode: string, totalSignatureCount: number) {
	if (!userCountryCode || !userStateCode || totalSignatureCount < 1) {
		return false;
	}
	return true;
}

function getUserStateName(userCountryCode: string, userStateCode: string) {
	if (userCountryCode === 'US' && userStateCode) {
		return getUsState(userStateCode);
	}
	return undefined;
}

export function usePpmuLocalizedHeadlineData(): (
	countryOrStateSignatureCounts: CountryOrStateSignatureCounts,
) => PpmuLocalizedHeadlineData | undefined {
	const { getCountry } = useI18n();
	const currentUser = useCurrentUser();

	return useCallback(
		(countryOrStateSignatureCounts: CountryOrStateSignatureCounts) => {
			const totalSignatureCount = countryOrStateSignatureCounts?.totalSignatureCount ?? 0;
			const userCountryCode = currentUser?.country?.countryCode ?? '';
			const stateCode = currentUser?.stateCode ?? '';

			if (!validateData(userCountryCode, stateCode, totalSignatureCount)) {
				return undefined;
			}

			const userCountryData = getCountry(userCountryCode);

			return {
				totalSignatureCount,
				userCountryName: userCountryData?.name ?? '',
				userStateName: getUserStateName(userCountryCode, stateCode),
			};
		},
		[currentUser, getCountry],
	);
}
